<template>
  <div>
    <!-- 热敏打印控制面板 -->
    <div class="thermal-print-panel">
      <h3>维修工单热敏标签打印</h3>
      <div class="print-controls">
        <el-button type="primary" size="small" @click="showPrinterConfig">
          打印机配置
        </el-button>
        <el-button type="success" size="small" :disabled="repairData.length === 0" @click="previewLabels">
          标签预览
        </el-button>
        <el-button type="warning" size="small" :disabled="repairData.length === 0" @click="printLabels">
          开始打印
        </el-button>
      </div>
      
      <!-- 打印状态信息 -->
      <div class="print-status">
        <p><strong>待打印工单：</strong>{{repairData.length}} 条</p>
        <p><strong>预计标签数量：</strong>{{repairData.length * 1}} 个</p>
        <p v-if="printerStatus" class="printer-status">
          <strong>打印机状态：</strong>
          <span :class="printerStatus.type">{{printerStatus.message}}</span>
        </p>
      </div>
    </div>

    <!-- 标签预览区域 -->
    <div v-if="showPreview" class="label-preview">
      <h4>标签预览</h4>
      <div class="preview-container">
        <div v-for="(item, index) in repairData" :key="index" class="preview-item">
          <div class="preview-label">
            <div class="qr-preview">
              <div :id="'previewQr' + index" class="qr-code-preview"/>
            </div>
            <div class="content-preview">
              <div class="preview-text">{{item.barcode}}</div>
              <div class="preview-text">{{item.billNo}}</div>
              <div class="preview-text">{{item.terminalCustomerName}}</div>
              <div class="preview-text">{{item.receiveTime}}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BarTender热敏打印组件 -->
    <bar-tender-printer
        v-if="showBartenderComponent"
        :print-data="prepareThermalData"
        :show-config="showConfigDialog"
        :thermal-printer="true"
        @configSaved="onConfigSaved"
        @cancelConfig="onConfigCancel"
        @cancelPrint="onPrintCancel"
        @printComplete="onPrintComplete"
        @printError="onPrintError"
    />
  </div>
</template>

<script>
  import QRCode from "qrcodejs2";
  import BarTenderPrinter from "@/components/BarTenderPrinter.vue";

  export default {
    name: "RepairWorkOrderThermalPrint",
    components: {
      BarTenderPrinter,
    },
  
    data() {
      return {
        repairData: [],
        showBartenderComponent: false,
        showConfigDialog: false,
        showPreview: false,
        printerStatus: null,
        thermalConfig: null,
        isPrinting: false,
      };
    },

    computed: {
      // 准备热敏打印数据
      prepareThermalData() {
        return this.repairData.map(item => ({
          barcode: item.barcode || "",
          billNo: item.billNo || "",
          terminalCustomerName: item.terminalCustomerName || "",
          receiveTime: item.receiveTime || "",
          pkid: item.pkid || "",
          repairType: item.repairType || "",
          equipmentName: item.equipmentName || "",
          faultDescription: item.faultDescription || "",
          // 热敏标签专用字段
          labelType: "thermal",
          printCopies: 2, // 每个工单打印1个标签
          thermalSettings: {
            darkness: (this.thermalConfig && this.thermalConfig.darkness) || 8,
            speed: (this.thermalConfig && this.thermalConfig.speed) || 3,
            width: (this.thermalConfig && this.thermalConfig.width) || 86,
            height: (this.thermalConfig && this.thermalConfig.height) || 15,
          },
        }));
      },
    },

    mounted() {
      this.loadRepairData();
      this.checkPrinterStatus();
    },

    methods: {
      // 加载维修工单数据
      loadRepairData() {
        try {
          this.repairData = JSON.parse(this.$route.query.params);
          console.log("已加载维修工单数据:", this.repairData.length, "条");
        } catch (error) {
          console.error("加载维修工单数据失败:", error);
          this.$message.error("数据加载失败");
          this.repairData = [];
        }
      },

      // 检查打印机状态
      checkPrinterStatus() {
        // 模拟检查打印机状态
        setTimeout(() => {
          this.printerStatus = {
            type: "success",
            message: "热敏打印机就绪",
          };
        }, 1000);
      },

      // 显示打印机配置
      showPrinterConfig() {
        this.showConfigDialog = true;
        this.showBartenderComponent = true;
      },

      // 预览标签
      previewLabels() {
        if (this.repairData.length === 0) {
          this.$message.warning("没有可预览的数据");
          return;
        }

        this.showPreview = true;
        this.$nextTick(() => {
          this.generatePreviewQRCodes();
        });
      },

      // 生成预览二维码
      generatePreviewQRCodes() {
        this.repairData.forEach((item, index) => {
          const elementId = `previewQr${index}`;
          const element = document.getElementById(elementId);
          if (element) {
            element.innerHTML = "";
            new QRCode(element, {
              text: item.barcode,
              width: 40,
              height: 40,
              colorDark: "#000000",
              colorLight: "#ffffff",
              correctLevel: QRCode.CorrectLevel.M,
            });
          }
        });
      },

      // 开始打印
      printLabels() {
        if (this.repairData.length === 0) {
          this.$message.warning("没有可打印的数据");
          return;
        }

        if (this.isPrinting) {
          this.$message.warning("正在打印中，请稍候...");
          return;
        }

        // 检查是否有保存的配置
        const savedConfig = localStorage.getItem("thermalPrinterConfig");
        if (!savedConfig) {
          this.$message.warning("请先配置热敏打印机");
          this.showPrinterConfig();
          return;
        }

        try {
          this.thermalConfig = JSON.parse(savedConfig);
        
          // 验证配置完整性
          if (!this.thermalConfig.printerName) {
            this.$message.warning("请完善打印机配置信息");
            this.showPrinterConfig();
            return;
          }

          this.isPrinting = true;
          this.printerStatus = {
            type: "info",
            message: "正在准备打印...",
          };

          // 执行热敏打印
          this.executeThermalPrint();
        
        } catch (error) {
          console.error("读取配置失败:", error);
          this.$message.error("读取配置失败，请重新配置");
          this.showPrinterConfig();
        }
      },

      // 执行热敏打印
      executeThermalPrint() {
        this.showBartenderComponent = true;
      
        // 模拟打印过程
        // setTimeout(() => {
        //   this.printerStatus = {
        //     type: "info",
        //     message: `正在打印 ${this.repairData.length} 个工单标签...`,
        //   };
        // }, 1000);
      },

      // 配置保存
      onConfigSaved(configData) {
        this.thermalConfig = configData.config;
        this.showBartenderComponent = false;
        this.showConfigDialog = false;
      
        this.$message.success("热敏打印机配置已保存");
      
        if (configData.autoPrint) {
          setTimeout(() => {
            this.printLabels();
          }, 500);
        }
      },

      // 配置取消
      onConfigCancel() {
        this.showBartenderComponent = false;
        this.showConfigDialog = false;
      },

      // 打印取消
      onPrintCancel() {
        this.isPrinting = false;
        this.showBartenderComponent = false;
        this.printerStatus = {
          type: "warning",
          message: "打印已取消",
        };
      },

      // 打印完成
      onPrintComplete(result) {
        this.isPrinting = false;
        this.showBartenderComponent = false;
        this.printerStatus = {
          type: "success",
          message: `打印完成！共打印 ${result.printedCount} 个标签`,
        };
        this.$message.success("热敏标签打印完成！");
      },

      // 打印错误
      onPrintError(error) {
        this.isPrinting = false;
        this.showBartenderComponent = false;
        this.printerStatus = {
          type: "error",
          message: `打印失败: ${error.message}`,
        };
        this.$message.error(`打印失败: ${error.message}`);
      },
    },
  };
</script>

<style lang="scss" scoped>
.thermal-print-panel {
  max-width: 600px;
  margin: 20px auto;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);

  h3 {
    margin: 0 0 20px 0;
    color: #495057;
    font-size: 20px;
    text-align: center;
    font-weight: 600;
  }

  .print-controls {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .print-status {
    background: white;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    
    p {
      margin: 8px 0;
      font-size: 14px;
      color: #495057;
      
      strong {
        color: #212529;
      }
    }

    .printer-status {
      .success {
        color: #28a745;
        font-weight: 600;
      }
      
      .error {
        color: #dc3545;
        font-weight: 600;
      }
      
      .info {
        color: #17a2b8;
        font-weight: 600;
      }
      
      .warning {
        color: #ffc107;
        font-weight: 600;
      }
    }
  }
}

.label-preview {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  h4 {
    margin: 0 0 15px 0;
    color: #495057;
    font-size: 16px;
    text-align: center;
  }

  .preview-container {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    justify-content: center;
    max-height: 400px;
    overflow-y: auto;
    padding: 10px;
  }

  .preview-item {
    flex: 0 0 auto;
  }

  .preview-label {
    width: 86mm;
    height: 15mm;
    display: flex;
    align-items: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 2mm;
    background: white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .qr-preview {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .qr-code-preview {
    width: 40px;
    height: 40px;
  }

  .content-preview {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .preview-text {
    font-family: "Arial", "Microsoft YaHei", sans-serif;
    color: #000;
    font-size: 8pt;
    font-weight: 600;
    line-height: 1.1;
    margin-bottom: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .preview-text:last-child {
    margin-bottom: 0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .thermal-print-panel {
    margin: 10px;
    padding: 20px;
    
    .print-controls {
      flex-direction: column;
      align-items: center;
    }
  }
  
  .label-preview {
    margin: 10px;
    padding: 15px;
    
    .preview-container {
      flex-direction: column;
      align-items: center;
    }
  }
}

// 打印样式（仅用于预览）
@media print {
  .thermal-print-panel {
    display: none;
  }
}
</style>